import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../lib/auth-context';
import { createApiClient } from '../lib/api';

type Period = 'current_month' | 'last_month' | '3_month' | 'custom';

const PERIOD_LABELS: Record<Exclude<Period, 'custom'>, string> = {
  current_month: 'Current Month',
  last_month: 'Last Month',
  '3_month': '3-Month Trend',
};

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
      <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-semibold text-white mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

function formatCurrency(val: number): string {
  return `$${val.toFixed(2)}`;
}

function formatPercent(val: number): string {
  return `${val.toFixed(1)}%`;
}

function toDateStr(d: Date): string {
  return d.toISOString().substring(0, 10);
}

export function FinOpsPage() {
  const { getValidToken } = useAuth();
  const [period, setPeriod] = useState<Period>('current_month');
  const [startDate, setStartDate] = useState(() => {
    // Default custom range: today's date
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return toDateStr(d);
  });
  const [endDate, setEndDate] = useState(() => toDateStr(new Date()));

  const queryPeriod = period;
  const queryStart = period === 'custom' ? startDate : undefined;
  const queryEnd = period === 'custom' ? endDate : undefined;

  const usageQuery = useQuery({
    queryKey: ['finops-usage', queryPeriod, queryStart, queryEnd],
    queryFn: async () => {
      const token = await getValidToken();
      return createApiClient(token).getFinopsUsage(queryPeriod, queryStart, queryEnd);
    },
    staleTime: 5 * 60 * 1000,
  });

  const costsQuery = useQuery({
    queryKey: ['finops-costs', queryPeriod, queryStart, queryEnd],
    queryFn: async () => {
      const token = await getValidToken();
      return createApiClient(token).getFinopsCosts(queryPeriod, queryStart, queryEnd);
    },
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = usageQuery.isLoading || costsQuery.isLoading;
  const error = usageQuery.error || costsQuery.error;

  // Get the latest (or only) period from each response
  const usagePeriods = usageQuery.data?.periods || [];
  const costPeriods = costsQuery.data?.periods || [];
  const latestUsage = usagePeriods[usagePeriods.length - 1];
  const latestCosts = costPeriods[costPeriods.length - 1];
  const users = usageQuery.data?.users;
  const tierProfitability = costsQuery.data?.tierProfitability || [];

  // Aggregate across all periods for custom ranges spanning multiple months
  const aggregatedCosts = period === 'custom' && costPeriods.length > 1
    ? aggregatePeriods(costPeriods)
    : null;
  const displayCosts = aggregatedCosts || latestCosts;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold text-white">FinOps Dashboard</h1>
        <div className="flex items-center gap-2">
          {(Object.entries(PERIOD_LABELS) as [Exclude<Period, 'custom'>, string][]).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setPeriod(key)}
              className={`px-3 py-1.5 text-xs rounded ${
                period === key
                  ? 'bg-orange-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              {label}
            </button>
          ))}
          <button
            onClick={() => setPeriod('custom')}
            className={`px-3 py-1.5 text-xs rounded ${
              period === 'custom'
                ? 'bg-orange-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            Custom Range
          </button>
        </div>
      </div>

      {period === 'custom' && (
        <div className="flex items-center gap-3 mb-4">
          <label className="text-xs text-slate-400">From</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-white"
          />
          <label className="text-xs text-slate-400">To</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-white"
          />
        </div>
      )}

      {latestUsage && period !== 'custom' && (
        <div className="text-xs text-slate-500 mb-4">
          Showing: {latestUsage.period.label}
        </div>
      )}

      {period === 'custom' && (
        <div className="text-xs text-slate-500 mb-4">
          Showing: {startDate} to {endDate}
        </div>
      )}

      {error && (
        <div className="bg-red-900/30 border border-red-800 rounded-lg p-4 mb-6 text-sm text-red-300">
          {(error as Error).message}
          <button
            onClick={() => { usageQuery.refetch(); costsQuery.refetch(); }}
            className="ml-3 text-red-400 underline hover:text-red-200"
          >
            Retry
          </button>
        </div>
      )}

      {isLoading && <div className="text-slate-400">Loading...</div>}

      {/* Usage Metrics */}
      {latestUsage && (
        <section className="mb-8">
          <h2 className="text-sm font-medium text-slate-300 mb-3">Usage Metrics</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard label="DAU" value={String(latestUsage.usage.dau)} sub="Most recent day" />
            <StatCard label="MAU" value={String(latestUsage.usage.mau)} />
            <StatCard
              label="Analyses"
              value={String(latestUsage.usage.completedAnalyses)}
              sub={`${latestUsage.usage.totalSessions} total sessions`}
            />
            <StatCard
              label="Analyses / User"
              value={latestUsage.usage.analysesPerUser.toFixed(1)}
            />
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 text-sm">
            <div className="flex gap-6 text-slate-300 mb-3">
              <span>
                Completion: {latestUsage.usage.completedAnalyses} / {latestUsage.usage.totalSessions}{' '}
                ({formatPercent(latestUsage.usage.completionRate * 100)})
              </span>
              <span>Failed: {latestUsage.usage.failedAnalyses}</span>
              <span>Needs Followup: {latestUsage.usage.needsFollowup}</span>
            </div>
            {users && (
              <div className="text-slate-400">
                <span className="text-slate-500 mr-2">Users by Tier:</span>
                {Object.entries(users.byTier)
                  .sort(([, a], [, b]) => (b as number) - (a as number))
                  .map(([tier, count]) => (
                    <span key={tier} className="mr-4">
                      {tier}: {count as number} ({formatPercent(((count as number) / users.total) * 100)})
                    </span>
                  ))}
                <span className="ml-2 text-slate-500">Total: {users.total}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* AWS Costs */}
      {displayCosts && (
        <section className="mb-8">
          <h2 className="text-sm font-medium text-slate-300 mb-3">AWS Costs</h2>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <div className="flex items-baseline gap-3 mb-1">
              <span className="text-2xl font-semibold text-white">
                {formatCurrency(displayCosts.costs.total)}
              </span>
              <span className="text-xs text-slate-500">USD</span>
              {displayCosts.costs.isEstimated && (
                <span className="text-xs text-amber-500">(estimated)</span>
              )}
            </div>
            <div className="text-xs text-slate-500 mb-4">
              Bedrock total: {formatCurrency(displayCosts.costs.bedrockTotal)} ({formatPercent(
                displayCosts.costs.total > 0
                  ? (displayCosts.costs.bedrockTotal / displayCosts.costs.total) * 100
                  : 0
              )} of total)
            </div>

            {/* Bedrock model breakdown */}
            {Object.keys(displayCosts.costs.bedrockByModel).length > 0 && (
              <div className="mb-4">
                <div className="text-xs text-slate-500 mb-2">Bedrock by Model</div>
                <table className="w-full text-sm">
                  <tbody>
                    {Object.entries(displayCosts.costs.bedrockByModel)
                      .sort(([, a], [, b]) => (b as number) - (a as number))
                      .map(([model, cost]) => (
                        <tr key={model} className="border-t border-slate-800">
                          <td className="py-1.5 text-slate-300 pl-4">{model}</td>
                          <td className="py-1.5 text-right text-white w-24">{formatCurrency(cost as number)}</td>
                          <td className="py-1.5 text-right text-slate-500 w-20">
                            {formatPercent(displayCosts.costs.bedrockTotal > 0
                              ? ((cost as number) / displayCosts.costs.bedrockTotal) * 100
                              : 0)}
                          </td>
                        </tr>
                      ))}
                    <tr className="border-t border-slate-700">
                      <td className="py-1.5 text-slate-400 pl-4 font-medium">Subtotal</td>
                      <td className="py-1.5 text-right text-white font-medium w-24">{formatCurrency(displayCosts.costs.bedrockTotal)}</td>
                      <td className="py-1.5 text-right text-slate-500 w-20">
                        {formatPercent(displayCosts.costs.total > 0
                          ? (displayCosts.costs.bedrockTotal / displayCosts.costs.total) * 100
                          : 0)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Service breakdown (excluding Bedrock, shown above) */}
            <div className="text-xs text-slate-500 mb-2">Other Services</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-500 text-xs">
                  <th className="text-left py-1 font-medium">Service</th>
                  <th className="text-right py-1 font-medium w-24">Cost</th>
                  <th className="text-right py-1 font-medium w-20">%</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const nonBedrockEntries = Object.entries(displayCosts.costs.byService)
                    .filter(([service]) => !service.includes('Bedrock'));
                  const nonBedrockTotal = nonBedrockEntries.reduce((sum, [, cost]) => sum + (cost as number), 0);
                  return (
                    <>
                      {nonBedrockEntries
                        .sort(([, a], [, b]) => (b as number) - (a as number))
                        .map(([service, cost]) => (
                          <tr key={service} className="border-t border-slate-800">
                            <td className="py-1.5 text-slate-300">{service}</td>
                            <td className="py-1.5 text-right text-white">{formatCurrency(cost as number)}</td>
                            <td className="py-1.5 text-right text-slate-500">
                              {formatPercent(displayCosts.costs.total > 0
                                ? ((cost as number) / displayCosts.costs.total) * 100
                                : 0)}
                            </td>
                          </tr>
                        ))}
                      <tr className="border-t border-slate-700">
                        <td className="py-1.5 text-slate-400 font-medium">Subtotal</td>
                        <td className="py-1.5 text-right text-white font-medium">{formatCurrency(nonBedrockTotal)}</td>
                        <td className="py-1.5 text-right text-slate-500">
                          {formatPercent(displayCosts.costs.total > 0
                            ? (nonBedrockTotal / displayCosts.costs.total) * 100
                            : 0)}
                        </td>
                      </tr>
                    </>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Unit Economics */}
      {displayCosts && (
        <section className="mb-8">
          <h2 className="text-sm font-medium text-slate-300 mb-3">Unit Economics</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard
              label="Cost / Analysis"
              value={`$${displayCosts.unitEconomics.costPerAnalysis.toFixed(4)}`}
            />
            <StatCard
              label="Cost / Customer"
              value={`$${displayCosts.unitEconomics.costPerCustomer.toFixed(4)}`}
            />
            <StatCard
              label="Bedrock / Analysis"
              value={`$${displayCosts.unitEconomics.bedrockCostPerAnalysis.toFixed(4)}`}
              sub={`${displayCosts.unitEconomics.bedrockPercentOfTotal}% of unit cost`}
            />
            <StatCard
              label="Analyses"
              value={String(displayCosts.usage.completedAnalyses)}
              sub={`${displayCosts.usage.mau} active users`}
            />
          </div>

          {/* Tier Profitability */}
          {tierProfitability.length > 0 && (
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
              <div className="text-xs text-slate-500 mb-2">Tier Profitability (at current unit cost)</div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-500 text-xs">
                    <th className="text-left py-1 font-medium">Tier</th>
                    <th className="text-right py-1 font-medium">Price</th>
                    <th className="text-right py-1 font-medium">Scans</th>
                    <th className="text-right py-1 font-medium">$/Scan</th>
                    <th className="text-right py-1 font-medium">Scan Cost</th>
                    <th className="text-right py-1 font-medium">Infra/User</th>
                    <th className="text-right py-1 font-medium">Total Cost</th>
                    <th className="text-right py-1 font-medium">Margin</th>
                    <th className="text-right py-1 font-medium">Margin %</th>
                  </tr>
                </thead>
                <tbody>
                  {tierProfitability.map((tier: any) => (
                    <tr key={tier.tier} className="border-t border-slate-800">
                      <td className="py-1.5 text-slate-300 font-medium">{tier.tier}</td>
                      <td className="py-1.5 text-right text-white">{formatCurrency(tier.price)}</td>
                      <td className="py-1.5 text-right text-slate-400">{tier.scansIncluded}</td>
                      <td className="py-1.5 text-right text-slate-400">${tier.costPerScan.toFixed(4)}</td>
                      <td className="py-1.5 text-right text-slate-400">{formatCurrency(tier.totalScanCost)}</td>
                      <td className="py-1.5 text-right text-slate-400">${tier.infraCostPerUser?.toFixed(4) ?? '—'}</td>
                      <td className="py-1.5 text-right text-white">{formatCurrency(tier.totalCost ?? tier.totalScanCost)}</td>
                      <td className={`py-1.5 text-right font-medium ${tier.margin >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(tier.margin)}
                      </td>
                      <td className={`py-1.5 text-right ${tier.marginPercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatPercent(tier.marginPercent)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Multi-period Trend (3-month or custom spanning multiple months) */}
      {costPeriods.length > 1 && (
        <section className="mb-8">
          <h2 className="text-sm font-medium text-slate-300 mb-3">
            {period === '3_month' ? '3-Month Trend' : 'Period Breakdown'}
          </h2>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-500 text-xs">
                  <th className="text-left py-1 font-medium">Month</th>
                  <th className="text-right py-1 font-medium">MAU</th>
                  <th className="text-right py-1 font-medium">Analyses</th>
                  <th className="text-right py-1 font-medium">AWS Cost</th>
                  <th className="text-right py-1 font-medium">$/Analysis</th>
                  <th className="text-right py-1 font-medium">$/Customer</th>
                  <th className="text-right py-1 font-medium">Bedrock</th>
                </tr>
              </thead>
              <tbody>
                {costPeriods.map((p: any, i: number) => {
                  const usagePeriod = usagePeriods[i];
                  return (
                    <tr key={p.period.label} className="border-t border-slate-800">
                      <td className="py-1.5 text-slate-300">{p.period.label}</td>
                      <td className="py-1.5 text-right text-white">{usagePeriod?.usage.mau ?? p.usage.mau}</td>
                      <td className="py-1.5 text-right text-white">{p.usage.completedAnalyses}</td>
                      <td className="py-1.5 text-right text-white">{formatCurrency(p.costs.total)}</td>
                      <td className="py-1.5 text-right text-slate-400">${p.unitEconomics.costPerAnalysis.toFixed(4)}</td>
                      <td className="py-1.5 text-right text-slate-400">${p.unitEconomics.costPerCustomer.toFixed(4)}</td>
                      <td className="py-1.5 text-right text-slate-400">{formatCurrency(p.costs.bedrockTotal)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

/** Aggregate multiple periods into one summary (for custom ranges spanning months) */
function aggregatePeriods(periods: any[]) {
  const totalCosts: Record<string, number> = {};
  const totalBedrockByModel: Record<string, number> = {};
  let total = 0;
  let bedrockTotal = 0;
  let completedAnalyses = 0;
  let totalSessions = 0;
  const allUsers = new Set<string>();
  let isEstimated = false;

  for (const p of periods) {
    total += p.costs.total;
    bedrockTotal += p.costs.bedrockTotal;
    completedAnalyses += p.usage.completedAnalyses;
    totalSessions += p.usage.totalSessions;
    if (p.costs.isEstimated) isEstimated = true;

    for (const [svc, cost] of Object.entries(p.costs.byService)) {
      totalCosts[svc] = (totalCosts[svc] || 0) + (cost as number);
    }
    for (const [model, cost] of Object.entries(p.costs.bedrockByModel)) {
      totalBedrockByModel[model] = (totalBedrockByModel[model] || 0) + (cost as number);
    }
  }

  // Use max MAU across periods as approximation
  const mau = Math.max(...periods.map((p: any) => p.usage.mau));

  return {
    costs: {
      total: Math.round(total * 100) / 100,
      byService: totalCosts,
      bedrockTotal: Math.round(bedrockTotal * 100) / 100,
      bedrockByModel: totalBedrockByModel,
      currency: 'USD',
      isEstimated,
    },
    usage: { completedAnalyses, totalSessions, mau },
    unitEconomics: {
      costPerAnalysis: completedAnalyses > 0 ? Math.round((total / completedAnalyses) * 10000) / 10000 : 0,
      costPerCustomer: mau > 0 ? Math.round((total / mau) * 10000) / 10000 : 0,
      bedrockCostPerAnalysis: completedAnalyses > 0 ? Math.round((bedrockTotal / completedAnalyses) * 10000) / 10000 : 0,
      bedrockPercentOfTotal: total > 0 ? Math.round((bedrockTotal / total) * 1000) / 10 : 0,
    },
  };
}
